import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Users, Workflow, EyeOff, Target, Megaphone, LifeBuoy,
  BarChart3, Layers, BadgeCheck, SlidersHorizontal, TrendingUp, Check,
  Globe, Network, Clock,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal, KineticTitle } from "@/components/Reveal";
import { Panel } from "@/components/Panel";
import CTABand from "@/components/CTABand";

const trustIndustries = [
  "Contact Center", "Telecom", "Technology", "Financial",
  "Fintech", "Healthcare", "Insurance", "SaaS",
];

const symptoms = [
  {
    icon: Users,
    title: "Disconnected teams",
    text: "Handoffs break between marketing, sales, and CS — and pipeline leaks at every seam.",
  },
  {
    icon: Workflow,
    title: "Inefficient workflows",
    text: "Reps spend more time maintaining the CRM than selling in it.",
  },
  {
    icon: EyeOff,
    title: "Limited visibility",
    text: "Leadership makes decisions on lagging, conflicting numbers.",
  },
];

const whyItMatters = [
  { title: "Team alignment", text: "Shared definitions, shared goals, shared pipeline." },
  { title: "Process efficiency", text: "Less manual work, faster cycles, cleaner data." },
  { title: "Predictable revenue", text: "Forecasts you can actually run the business on." },
];

const disciplines = [
  {
    icon: Target,
    title: "Sales Operations",
    text: "CRM strategy, forecasting, and pipeline management.",
  },
  {
    icon: Megaphone,
    title: "Marketing Operations",
    text: "Funnel optimization, campaign reporting, and automation.",
  },
  {
    icon: LifeBuoy,
    title: "Customer Success Operations",
    text: "Churn reduction strategies and retention processes.",
  },
  {
    icon: BarChart3,
    title: "Data & Analytics",
    text: "Insightful dashboards, KPI tracking, and actionable recommendations.",
  },
  {
    icon: Layers,
    title: "Tech Stack Optimization",
    text: "Tool integration, system evaluation, and tech ROI analysis.",
  },
];

const reasons = [
  {
    icon: BadgeCheck,
    title: "Proven expertise.",
    text: "Deep, hands-on knowledge of RevOps strategy across eight industries.",
  },
  {
    icon: SlidersHorizontal,
    title: "Tailored solutions.",
    text: "Recommendations built around your business goals — never a template.",
  },
  {
    icon: TrendingUp,
    title: "Results-focused.",
    text: "We commit to measurable outcomes, then we build the reporting to prove them.",
  },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const markY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const reduced =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  return (
    <div data-testid="home-page">
      <SEO
        title="RevOps Consulting That Accelerates Revenue Growth | RevGen Genius"
        description="RevGen Genius is a RevOps consultancy that aligns sales, marketing and customer success into one predictable revenue engine — across markets and time zones."
        keywords="RevOps consulting, revenue operations consultant, sales and marketing alignment, CRM strategy consulting, international RevOps consulting, multi-market revenue operations"
        path="/"
      />

      {/* Hero band — floating blue block with kinetic headline */}
      <div className="px-4 pt-[92px] sm:px-6 lg:px-10">
        <section
          ref={heroRef}
          data-testid="home-hero"
          className="relative mx-auto max-w-[1360px] overflow-hidden rounded-[32px] bg-[#1F5FFF]"
        >
          <motion.img
            src="/assets/revgengenius-mark-white.png"
            alt=""
            aria-hidden="true"
            style={{ y: reduced ? 0 : markY }}
            className="pointer-events-none absolute -bottom-28 -right-20 w-[400px] opacity-[0.14] sm:w-[560px]"
          />
          <div className="relative px-6 py-20 sm:px-12 sm:py-28 lg:px-20 lg:py-32">
            <motion.span
              initial={reduced ? false : { opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="inline-block rounded-full border px-5 py-2 font-heading text-[0.72rem] font-semibold uppercase tracking-[0.14em]"
              style={{ color: "#FFFFFF", borderColor: "rgba(255,255,255,0.35)" }}
            >
              Revenue Operations Consulting
            </motion.span>
            <KineticTitle
              lines={["Accelerate revenue growth", "with strategic", "RevOps consulting."]}
              className="h1 mt-7 max-w-4xl text-white"
            />
            <motion.p
              initial={reduced ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.62, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-xl text-[1.0625rem] leading-relaxed text-white/85"
            >
              We unite sales, marketing, and customer success into a single, data-driven
              revenue engine — so your teams stop guessing and start compounding.
            </motion.p>
            <motion.div
              initial={reduced ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.78, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link to="/contact" data-testid="hero-book-assessment-button" className="btn-white">
                Book a Free Assessment <ArrowRight size={18} />
              </Link>
              <Link to="/approach" data-testid="hero-how-we-work-button" className="btn-outline-white">
                See How We Work
              </Link>
            </motion.div>
          </div>
        </section>
      </div>

      {/* Trust strip */}
      <Reveal className="mx-auto max-w-[1240px] px-5 pt-14 sm:px-8">
        <div data-testid="trust-strip" className="text-center">
          <p className="font-heading text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-[#5B6784]">
            Experience across
          </p>
          <ul className="mt-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
            {trustIndustries.map((t, i) => (
              <li key={t} className="flex items-center gap-3">
                <span className="font-heading text-sm font-semibold text-[#0A1F44] sm:text-base">{t}</span>
                {i < trustIndustries.length - 1 && (
                  <span className="h-1.5 w-1.5 rounded-full bg-[#1F5FFF]" aria-hidden="true" />
                )}
              </li>
            ))}
          </ul>
        </div>
      </Reveal>

      <div className="mx-auto max-w-[1240px] space-y-24 px-5 pt-20 sm:px-8 sm:pt-24">
        {/* The Problem */}
        <Reveal>
          <Panel label="The Problem" className="pt-16">
            <div className="max-w-3xl">
              <h2 className="h2">Most revenue teams aren't underperforming. They're disconnected.</h2>
              <p className="lead mt-5">
                Sales, marketing, and customer success each run their own playbook, their own tools,
                and their own definition of a qualified lead. The result is predictable: manual
                workflows nobody owns, forecasts nobody trusts, and revenue metrics nobody can see
                clearly enough to act on.
              </p>
            </div>
            <p className="mt-12 font-heading text-sm font-semibold uppercase tracking-[0.14em] text-[#1F5FFF]">
              Three symptoms we see in almost every engagement:
            </p>
            <div className="mt-8 grid gap-10 md:grid-cols-3">
              {symptoms.map((s, i) => (
                <Reveal key={s.title} delay={i * 0.1}>
                  <div data-testid={`symptom-${i}`} className="flex gap-4">
                    <s.icon size={28} strokeWidth={1.8} className="mt-1 shrink-0 text-[#1F5FFF]" aria-hidden="true" />
                    <div>
                      <h3 className="font-heading text-lg font-semibold">{s.title}.</h3>
                      <p className="mt-2 text-[0.95rem] leading-relaxed text-[#5B6784]">{s.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Panel>
        </Reveal>

        {/* The Solution */}
        <Reveal>
          <Panel label="The Solution" tint className="pt-16">
            <div className="grid items-center gap-12 lg:grid-cols-2">
              <div>
                <h2 className="h2">One system. One set of numbers. One growth motion.</h2>
                <p className="lead mt-5">
                  RevOps replaces three separate operating models with one. We align the teams,
                  redesign the workflows, and build the reporting layer that makes revenue
                  predictable instead of surprising.
                </p>
                <p className="mt-10 font-heading text-sm font-semibold uppercase tracking-[0.14em] text-[#1F5FFF]">
                  Why it matters:
                </p>
                <ul className="mt-6 space-y-5">
                  {whyItMatters.map((w) => (
                    <li key={w.title} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#1F5FFF]">
                        <Check size={14} strokeWidth={3} className="text-white" aria-hidden="true" />
                      </span>
                      <p className="text-[0.98rem] leading-relaxed text-[#17203A]">
                        <strong className="font-semibold">{w.title}</strong> — {w.text}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative overflow-hidden rounded-[28px]">
                <img
                  src="https://images.unsplash.com/photo-1556761175-b413da4baf72?auto=format&fit=crop&w=1400&q=70"
                  alt="Consultant and client reviewing revenue dashboards together on a laptop"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
                <div className="absolute bottom-4 left-4 rounded-2xl bg-[#1F5FFF] px-5 py-3 font-heading text-sm font-semibold text-white shadow-[0_2px_12px_rgba(31,95,255,0.25)]">
                  One system. One set of numbers.
                </div>
              </div>
            </div>
          </Panel>
        </Reveal>

        {/* What We Do */}
        <Reveal>
          <Panel label="What We Do" className="pt-16">
            <h2 className="h2 max-w-2xl">Five operating disciplines, one revenue engine.</h2>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {disciplines.map((d, i) => (
                <Reveal key={d.title} delay={(i % 3) * 0.08}>
                  <div
                    data-testid={`discipline-card-${i}`}
                    className="card-lift h-full rounded-[28px] border-[1.5px] border-[#D7E3FB] bg-[#F8FBFF] p-8"
                  >
                    <d.icon size={28} strokeWidth={1.8} className="text-[#1F5FFF]" aria-hidden="true" />
                    <h3 className="font-heading mt-5 text-lg font-semibold">{d.title}</h3>
                    <p className="mt-3 text-[0.95rem] leading-relaxed text-[#5B6784]">{d.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Link
              to="/services"
              data-testid="home-explore-services-link"
              className="mt-10 inline-flex items-center gap-2 font-heading text-base font-semibold text-[#1F5FFF] transition-colors hover:text-[#1348D6]"
            >
              Explore our services <ArrowRight size={18} />
            </Link>
          </Panel>
        </Reveal>

        {/* Where We Work */}
        <Reveal>
          <Panel label="Where We Work" className="bg-[#F8FBFF] pt-16">
            <div className="max-w-3xl">
              <h2 className="h2">Revenue operations don't stop at a border.</h2>
              <p className="lead mt-5">
                A pipeline that leaks in Chicago leaks the same way in London. The systems
                change, the currencies change, the regulations change — but the operating
                principles that make revenue predictable are the same everywhere. That is
                why our work travels.
              </p>
            </div>
            <div className="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
              {[
                {
                  icon: Globe,
                  title: "Wherever you operate",
                  text: "We work with clients across markets and time zones. Engagements run remote-first by design, so where your teams sit doesn't limit who can fix your revenue engine.",
                },
                {
                  icon: Network,
                  title: "Built for expansion",
                  text: "Entering a new market exposes every weakness in your revenue operations at once — duplicate data, broken handoffs, forecasts nobody trusts. We build the system before you scale into it, not after.",
                },
                {
                  icon: Clock,
                  title: "One engine, many markets",
                  text: "Multi-region teams tend to grow multiple versions of the truth. We consolidate them into a single reporting layer, so leadership sees one set of numbers regardless of which market produced them.",
                },
              ].map((b, i) => (
                <Reveal key={b.title} delay={i * 0.1}>
                  <div data-testid={`where-we-work-${i}`}>
                    <b.icon size={28} strokeWidth={1.8} className="text-[#1F5FFF]" aria-hidden="true" />
                    <h3 className="font-heading mt-4 text-lg font-semibold">{b.title}</h3>
                    <p className="mt-2 text-[0.95rem] leading-relaxed text-[#5B6784]">{b.text}</p>
                  </div>
                </Reveal>
              ))}
            </div>
            <p className="mt-12 max-w-3xl text-lg font-medium leading-relaxed text-[#17203A]">
              Expanding into a new region? That's exactly the moment revenue operations
              start to matter most.
            </p>
          </Panel>
        </Reveal>

        {/* Why Choose RevGen Genius */}
        <Reveal>
          <Panel label="Why Choose RevGen Genius" className="pt-16">
            <h2 className="h2 max-w-2xl">Senior operators, not slide decks.</h2>
            <div className="mt-12 grid gap-10 md:grid-cols-3">
              {reasons.map((r, i) => (
                <Reveal key={r.title} delay={i * 0.1}>
                  <div data-testid={`reason-${i}`} className="flex gap-4">
                    <r.icon size={28} strokeWidth={1.8} className="mt-1 shrink-0 text-[#1F5FFF]" aria-hidden="true" />
                    <div>
                      <h3 className="font-heading text-lg font-semibold">{r.title}</h3>
                      <p className="mt-2 text-[0.95rem] leading-relaxed text-[#5B6784]">{r.text}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </Panel>
        </Reveal>

        <CTABand
          title="Ready to optimize your revenue operations?"
          text="Start with a free assessment. We'll evaluate your current operations, identify where revenue is leaking, and show you what a unified system would look like — no obligation."
          testId="home-cta-book-button"
        />
      </div>
    </div>
  );
}
